import { FC } from 'react';
import { useDispatch } from 'react-redux';

import { setUser } from '@zoopla/redux-reducer-user';
import { AuthModalMode, SocialAuthProps } from '../../AuthModal.types';
import SocialAuth from '../SignIn/SocialAuth';
import { User } from '../../../../graphql.generated';
import {
    StyledIntroText,
    StyledRegisterButton,
    StyledRegisterContainer,
    StyledSignInButton
} from './QuickAccess.styles';
import { EmailMedium } from '@zoopla-eng/zds-components/icons';
import triggerTracking from '@blueprint/tracking';
import { liverampAts } from '../../utils/liverampAts';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';

const socialCopy = {
    facebook: 'Continue with Facebook',
    google: 'Continue with Google'
};

export const eventCategory = 'registration signin quick access';

export type QuickAccessProps = Pick<
    SocialAuthProps,
    'close' | 'actionAfterAuth' | 'onSocialAuth'
> & {
    selectMode: (newMode: AuthModalMode) => void;
    introText: string;
};

export const QuickAccess: FC<React.PropsWithChildren<QuickAccessProps>> = ({
    close,
    actionAfterAuth,
    onSocialAuth,
    selectMode,
    introText
}) => {
    const [useLiveRampAts] = useOptimizelyFeatureFlag(
        'client-cmp-108-liveramp-ats'
    );

    const reduxDispatch = useDispatch();

    const onSocialAuthSuccess = async (data?: User) => {
        if (data) {
            if (useLiveRampAts && data.email) {
                liverampAts(data.email);
            }

            reduxDispatch(setUser(data));
        }

        if (onSocialAuth) {
            onSocialAuth(data);
        } else {
            close();
        }
    };

    const onSocialClickTracking = (type: 'facebook' | 'google') => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: `click_to_${type}`,
            eventLabel: actionAfterAuth?.eventLabel || 'register_modal'
        });
    };

    const passedActionAfterAuth = {
        action: onSocialAuthSuccess,
        eventLabel: actionAfterAuth?.eventLabel || 'register_modal',
        segment: actionAfterAuth?.segment
    };

    const selectModeAndTrack: QuickAccessProps['selectMode'] = (journey) => {
        selectMode(journey);

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: `click_to_${
                journey === 'register' ? 'register' : 'email_signin'
            }`,
            eventLabel: actionAfterAuth?.eventLabel || 'register_modal'
        });
    };

    return (
        <>
            <StyledIntroText>{introText}</StyledIntroText>
            <SocialAuth
                copy={socialCopy}
                type="signin"
                actionAfterAuth={passedActionAfterAuth}
                variant="vertical"
                onClickTracking={onSocialClickTracking}
            />
            <StyledSignInButton
                category="secondary"
                icon={<EmailMedium />}
                onClick={() => selectModeAndTrack('sign-in')}
                data-testid="quick-access-signin"
            >
                Sign in with your email
            </StyledSignInButton>
            <StyledRegisterContainer>
                <StyledIntroText>
                    No account?{' '}
                    <StyledRegisterButton
                        category="tertiary"
                        onClick={() => selectModeAndTrack('register')}
                        data-testid="quick-access-register"
                    >
                        Register
                    </StyledRegisterButton>
                </StyledIntroText>
            </StyledRegisterContainer>
        </>
    );
};
